import React, { useCallback, useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import {
  adminFetchBelastingdienstForm,
  adminDownloadBelastingdienstPdf,
} from "../core/axios/endpoints";
import { useBodyScrollLock } from "../core/scroll-lock";

/**
 * BelastingdienstFormModal
 *
 * Toont wat er op het Belastingdienst-formulier ON0202 ("Opgaaf startende
 * onderneming") komt te staan voordat de PDF gedownload wordt. De admin kan de
 * vier velden aanpassen die een keuze zijn (handelsnaam, startdatum, vraag 8a
 * en 8b); persoonsgegevens worden bewust niet hier bewerkt maar in de aanvraag
 * zelf, zodat er één bron van waarheid blijft.
 *
 * De PDF wordt niet opgeslagen: hij wordt bij elke download opnieuw gemaakt.
 */

/** Lange optieteksten van het formulier inkorten voor in een dropdown. */
const kort = (tekst, max = 90) =>
  tekst.length > max ? `${tekst.slice(0, max).trim()}…` : tekst;

/** dd-mm-jjjj naar het formaat van een date-input. */
const naarInputDatum = (parts) =>
  parts ? `${parts.jaar}-${parts.maand}-${parts.dag}` : "";

const toonDatum = (parts) =>
  parts ? `${parts.dag}-${parts.maand}-${parts.jaar}` : "-";

/**
 * Bij `responseType: blob` komt ook een foutantwoord als blob binnen, dus de
 * JSON eromheen moet er zelf uitgepeld worden.
 */
const leesFoutBody = async (err) => {
  const data = err?.response?.data;
  if (data instanceof Blob) {
    try {
      return JSON.parse(await data.text());
    } catch (e) {
      return null;
    }
  }
  return data ?? null;
};

const Veld = ({ label, hint, children }) => (
  <label className="block text-sm">
    <span className="text-xs font-semibold text-ink-subtlest">{label}</span>
    {children}
    {hint ? <span className="mt-1 block text-xs text-ink-subtlest">{hint}</span> : null}
  </label>
);

const inputClass =
  "mt-1 w-full rounded border-2 border-line bg-white px-2 py-1.5 text-sm text-ink placeholder:text-ink-subtlest focus:border-brand focus:outline-none disabled:bg-sunken disabled:opacity-50";

const BelastingdienstFormModal = ({
  isOpen,
  onClose,
  onGenerated,
  applicationId,
}) => {
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState(null);
  const [preview, setPreview] = useState(null);
  const [waarschuwingen, setWaarschuwingen] = useState([]);
  const [overrides, setOverrides] = useState(null);
  // De vaste antwoorden staan apart: dat zijn geen tekstvelden, dus ze doen
  // niet mee aan het terugschrijven van genormaliseerde waarden hieronder.
  const [keuzeState, setKeuzeState] = useState(null);
  // Gezet als de server het formulier weigerde wegens blokkerende problemen.
  const [geblokkeerd, setGeblokkeerd] = useState(false);

  useBodyScrollLock(isOpen);

  // Elke blur vuurt een nieuw verzoek af; alleen het laatste mag de weergave
  // aanraken, anders wint een traag antwoord van een oudere waarde.
  const verzoekTeller = useRef(0);

  const laad = useCallback(
    async (verzonden, keuzen) => {
      const mijnVerzoek = verzoekTeller.current + 1;
      verzoekTeller.current = mijnVerzoek;
      setLoading(true);
      setError(null);
      try {
        const res = await adminFetchBelastingdienstForm(applicationId, {
          ...(verzonden ?? {}),
          ...(keuzen ?? {}),
        });
        if (mijnVerzoek !== verzoekTeller.current) return;
        const data = res?.data ?? res;
        const nieuweWaarschuwingen = data?.warnings ?? [];
        setPreview(data);
        setWaarschuwingen(nieuweWaarschuwingen);
        if (!nieuweWaarschuwingen.some((w) => w.level === "error")) {
          setGeblokkeerd(false);
        }

        const vanServer = {
          handelsnaam: data?.input?.handelsnaam ?? "",
          straat: data?.input?.adres?.straat ?? "",
          huisnummer: data?.input?.adres?.huisnummer ?? "",
          toevoeging: data?.input?.adres?.toevoeging ?? "",
          postcode: data?.input?.adres?.postcode ?? "",
          plaats: data?.input?.adres?.plaats ?? "",
          telefoon: data?.input?.telefoon ?? "",
          geboortedatum: naarInputDatum(data?.input?.geboortedatum),
          bsn: data?.input?.bsn ?? "",
          startdatum: naarInputDatum(data?.input?.startdatum),
          omzetStartjaar: data?.input?.omzetStartjaar ?? "",
          kostenStartjaar: data?.input?.kostenStartjaar ?? "",
        };

        if (!verzonden) {
          // Eerste keer: het voorstel van de server wordt de inhoud van de
          // formuliervelden, zodat wat je ziet ook is wat er gegenereerd wordt.
          setOverrides(vanServer);
          setKeuzeState({
            keuzes: data?.input?.keuzes ?? {},
            activiteit: data?.input?.activiteit ?? 0,
            activiteitOmschrijving: data?.input?.activiteitOmschrijving ?? "",
            aantalAfnemers: data?.input?.aantalAfnemers ?? "",
            stukken: data?.input?.stukken ?? [],
          });
          return;
        }

        // Daarna alleen terugschrijven wat de server anders invulde dan wij
        // stuurden. Zo zie je meteen dat een leeggemaakt veld terugvalt op de
        // waarde uit de aanvraag. Een veld dat sinds het verzoek opnieuw is
        // aangeraakt blijft ongemoeid, anders zou je typwerk onder je handen
        // vandaan verdwijnen.
        setOverrides((prev) => {
          const volgende = { ...(prev ?? {}) };
          Object.keys(vanServer).forEach((veld) => {
            const verstuurd = verzonden[veld] ?? "";
            const onaangeroerd = (prev?.[veld] ?? "") === verstuurd;
            if (onaangeroerd && verstuurd !== vanServer[veld]) {
              volgende[veld] = vanServer[veld];
            }
          });
          return volgende;
        });
      } catch (e) {
        if (mijnVerzoek !== verzoekTeller.current) return;
        setError(
          e?.response?.data?.error ??
            e?.message ??
            "Het voorstel kon niet geladen worden.",
        );
      } finally {
        if (mijnVerzoek === verzoekTeller.current) setLoading(false);
      }
    },
    [applicationId],
  );

  useEffect(() => {
    if (!isOpen || !applicationId) return;
    setPreview(null);
    setOverrides(null);
    setKeuzeState(null);
    setWaarschuwingen([]);
    setGeblokkeerd(false);
    laad(null, null);
  }, [isOpen, applicationId, laad]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape" && !downloading) onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen, downloading, onClose]);

  const zet = (veld, waarde) =>
    setOverrides((prev) => ({ ...(prev ?? {}), [veld]: waarde }));

  /** Een vast antwoord wijzigen: direct herberekenen, want het zijn keuzes. */
  const zetKeuze = (wijziging) => {
    const volgende = { ...(keuzeState ?? {}), ...wijziging };
    setKeuzeState(volgende);
    if (overrides) laad(overrides, volgende);
  };

  // Waarschuwingen opnieuw laten berekenen zodra een veld verlaten wordt. De
  // blokkade wordt pas opgeheven als het antwoord binnen is en er geen fouten
  // meer zijn, zodat de knop niet onder de cursor verandert.
  const herbereken = () => {
    if (overrides) laad(overrides, keuzeState);
  };

  const download = async (force = false) => {
    setDownloading(true);
    setError(null);
    try {
      const blob = await adminDownloadBelastingdienstPdf(applicationId, {
        ...(overrides ?? {}),
        ...(keuzeState ?? {}),
        ...(force ? { force: true } : {}),
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = preview?.bestandsnaam ?? "belastingdienst-formulier.pdf";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      setGeblokkeerd(false);
      // Het formulier is gemaakt en de download loopt, dus deze modal is klaar.
      // onGenerated ververst de aanvraag, zodat de nieuwe activiteitenregel en
      // het opgeslagen bestand meteen in beeld staan.
      if (onGenerated) onGenerated();
      onClose();
    } catch (e) {
      const body = await leesFoutBody(e);
      if (e?.response?.status === 422) {
        // De server weigert een formulier met ontbrekende of foute gegevens.
        if (body?.warnings) setWaarschuwingen(body.warnings);
        setGeblokkeerd(true);
        setError(
          body?.error ??
            "Er zijn problemen met de gegevens van deze aanvraag.",
        );
      } else {
        setError(
          body?.error ??
            e?.message ??
            "Het formulier kon niet gegenereerd worden.",
        );
      }
    } finally {
      setDownloading(false);
    }
  };

  if (!isOpen) return null;
  if (typeof document === "undefined") return null;

  const input = preview?.input;
  const opties = preview?.opties;
  const fouten = waarschuwingen.filter((w) => w.level === "error");
  const aandacht = waarschuwingen.filter((w) => w.level !== "error");

  return ReactDOM.createPortal(
    <div
      className="fixed inset-0 z-[10000] flex items-start justify-center overflow-y-auto p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Belastingdienst-formulier"
    >
      {/*
        De achtergrond is een eigen laag en niet de scrollcontainer zelf: anders
        telt een klik op de scrollbalk ook als een klik naast de modal.
      */}
      <div
        className="fixed inset-0 bg-[#091E428A]"
        aria-hidden="true"
        onMouseDown={() => {
          if (!downloading) onClose();
        }}
      />
      <div className="relative my-8 w-full max-w-3xl rounded-md bg-white shadow-overlay">
        <div className="flex items-start justify-between gap-4 px-6 pt-6">
          <div>
            <h2 className="text-xl font-medium text-ink">
              Belastingdienst-formulier
            </h2>
            <p className="mt-1 text-sm text-ink-subtlest">
              Opgaaf startende onderneming (ON0202). Controleer de gegevens en
              download het ingevulde formulier.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={downloading}
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded text-ink-subtlest hover:bg-[#091E420F] hover:text-ink disabled:opacity-50"
            aria-label="Sluiten"
          >
            <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
              <path
                fillRule="evenodd"
                d="M4.3 4.3a1 1 0 011.4 0L10 8.6l4.3-4.3a1 1 0 111.4 1.4L11.4 10l4.3 4.3a1 1 0 11-1.4 1.4L10 11.4l-4.3 4.3a1 1 0 11-1.4-1.4L8.6 10 4.3 5.7a1 1 0 010-1.4z"
                clipRule="evenodd"
              />
            </svg>
          </button>
        </div>

        <div className="max-h-[70vh] overflow-y-auto px-6 py-4 text-sm text-ink">
          {loading && !preview ? (
            <div className="py-12 text-center text-sm text-ink-subtlest">
              Voorstel laden…
            </div>
          ) : error && !preview ? (
            <div className="rounded bg-[#FFECEB] px-3 py-2 text-sm text-[#AE2A19]">
              {error}
            </div>
          ) : input ? (
            <>
              {error ? (
                <div className="mb-4 rounded bg-[#FFECEB] px-3 py-2 text-sm text-[#AE2A19]">
                  {error}
                </div>
              ) : null}

              {fouten.length ? (
                <div className="mb-4 rounded bg-[#FFECEB] px-3 py-2 text-[#AE2A19]">
                  <p className="text-sm font-semibold">
                    Dit ontbreekt of klopt niet
                  </p>
                  <ul className="mt-2 space-y-1 text-sm">
                    {fouten.map((w, i) => (
                      <li key={`f-${i}`}>
                        <span className="font-medium">{w.veld}:</span> {w.bericht}
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}

              {aandacht.length ? (
                <div className="mb-4 rounded bg-[#FFF7D6] px-3 py-2 text-[#7F5F01]">
                  <p className="text-sm font-semibold">
                    Even controleren
                  </p>
                  <ul className="mt-2 space-y-1 text-sm">
                    {aandacht.map((w, i) => (
                      <li key={`a-${i}`}>
                        <span className="font-medium">{w.veld}:</span> {w.bericht}
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}

              <div className="grid gap-4 sm:grid-cols-2">
                <Veld label="Vraag 1a Handelsnaam">
                  <input
                    type="text"
                    value={overrides?.handelsnaam ?? ""}
                    onChange={(e) => zet("handelsnaam", e.target.value)}
                    onBlur={herbereken}
                    disabled={downloading}
                    className={inputClass}
                  />
                </Veld>

                <Veld
                  label="Vraag 1c Startdatum onderneming"
                  hint={
                    input.startdatumBron === "handmatig"
                      ? "Handmatig ingevuld"
                      : input.startdatumBron === "onbekend"
                      ? "Niet af te leiden uit de aanvraag"
                      : `Overgenomen uit de ${input.startdatumBron} (de laatste van factuur- en installatiedatum)`
                  }
                >
                  <input
                    type="date"
                    value={overrides?.startdatum ?? ""}
                    onChange={(e) => zet("startdatum", e.target.value)}
                    onBlur={herbereken}
                    disabled={downloading}
                    className={inputClass}
                  />
                </Veld>

                <Veld label="Vraag 8a Schatting omzet startjaar (€)">
                  <input
                    type="text"
                    inputMode="numeric"
                    value={overrides?.omzetStartjaar ?? ""}
                    onChange={(e) => zet("omzetStartjaar", e.target.value)}
                    onBlur={herbereken}
                    disabled={downloading}
                    className={inputClass}
                  />
                </Veld>

                <Veld
                  label="Vraag 8b Kosten en investeringen (€)"
                  hint={
                    input.aankoopbedragOpgegeven
                      ? `Opgegeven aankoopbedrag: ${input.aankoopbedragOpgegeven}, afgerond op hele euro's`
                      : "Geen aankoopbedrag in de aanvraag"
                  }
                >
                  <input
                    type="text"
                    inputMode="numeric"
                    value={overrides?.kostenStartjaar ?? ""}
                    onChange={(e) => zet("kostenStartjaar", e.target.value)}
                    onBlur={herbereken}
                    disabled={downloading}
                    className={inputClass}
                  />
                </Veld>
              </div>

              <div className="mt-6 rounded bg-sunken p-4">
                <p className="text-base font-semibold text-ink">
                  Gegevens van de aanvrager
                </p>
                <p className="mt-1 text-xs text-ink-subtlest">
                  Voorgevuld uit de aanvraag. Een wijziging hier geldt alleen
                  voor dit formulier; de aanvraag zelf blijft ongewijzigd.
                </p>

                <div className="mt-3 grid gap-4 sm:grid-cols-2">
                  <Veld label="Straatnaam (vraag 1b en 3)">
                    <input
                      type="text"
                      value={overrides?.straat ?? ""}
                      onChange={(e) => zet("straat", e.target.value)}
                      onBlur={herbereken}
                      disabled={downloading}
                      className={inputClass}
                    />
                  </Veld>

                  <div className="grid grid-cols-2 gap-3">
                    <Veld label="Huisnummer">
                      <input
                        type="text"
                        value={overrides?.huisnummer ?? ""}
                        onChange={(e) => zet("huisnummer", e.target.value)}
                        onBlur={herbereken}
                        disabled={downloading}
                        className={inputClass}
                      />
                    </Veld>
                    <Veld label="Toevoeging">
                      <input
                        type="text"
                        value={overrides?.toevoeging ?? ""}
                        onChange={(e) => zet("toevoeging", e.target.value)}
                        onBlur={herbereken}
                        disabled={downloading}
                        className={inputClass}
                      />
                    </Veld>
                  </div>

                  <Veld label="Postcode">
                    <input
                      type="text"
                      value={overrides?.postcode ?? ""}
                      onChange={(e) => zet("postcode", e.target.value)}
                      onBlur={herbereken}
                      disabled={downloading}
                      className={inputClass}
                    />
                  </Veld>

                  <Veld label="Plaats">
                    <input
                      type="text"
                      value={overrides?.plaats ?? ""}
                      onChange={(e) => zet("plaats", e.target.value)}
                      onBlur={herbereken}
                      disabled={downloading}
                      className={inputClass}
                    />
                  </Veld>

                  <Veld label="Telefoonnummer" hint="Tien cijfers, het vakje heeft tien hokjes">
                    <input
                      type="text"
                      inputMode="numeric"
                      value={overrides?.telefoon ?? ""}
                      onChange={(e) => zet("telefoon", e.target.value)}
                      onBlur={herbereken}
                      disabled={downloading}
                      className={inputClass}
                    />
                  </Veld>

                  <Veld label="Geboortedatum">
                    <input
                      type="date"
                      value={overrides?.geboortedatum ?? ""}
                      onChange={(e) => zet("geboortedatum", e.target.value)}
                      onBlur={herbereken}
                      disabled={downloading}
                      className={inputClass}
                    />
                  </Veld>

                  <Veld label="Burgerservicenummer" hint="Negen cijfers">
                    <input
                      type="text"
                      inputMode="numeric"
                      value={overrides?.bsn ?? ""}
                      onChange={(e) => zet("bsn", e.target.value)}
                      onBlur={herbereken}
                      disabled={downloading}
                      className={`${inputClass} tabular-nums`}
                    />
                  </Veld>
                </div>
              </div>

              <details className="mt-4 rounded border border-line bg-white p-4">
                <summary className="cursor-pointer text-sm font-semibold text-ink hover:text-brand">
                  Vaste antwoorden aanpassen
                  <span className="ml-2 font-normal text-ink-subtlest">
                    (vraag 2, 6a, 7, 8c, 8d, 8e, 9 en 10)
                  </span>
                </summary>
                <p className="mt-2 text-xs text-ink-subtlest">
                  Deze staan goed voor een gewone thuisbatterij-aanvraag. Wijk je
                  ervan af, dan krijg je daar een waarschuwing over.
                </p>

                <div className="mt-3 space-y-3">
                  {opties
                    ? Object.entries(opties.keuzevelden).map(
                        ([sleutel, veld]) => (
                          <Veld key={sleutel} label={veld.vraag}>
                            <select
                              value={keuzeState?.keuzes?.[sleutel] ?? 0}
                              onChange={(e) =>
                                zetKeuze({
                                  keuzes: {
                                    ...(keuzeState?.keuzes ?? {}),
                                    [sleutel]: Number(e.target.value),
                                  },
                                })
                              }
                              disabled={downloading}
                              className={inputClass}
                            >
                              {veld.opties.map((o) => (
                                <option key={o.index} value={o.index}>
                                  {kort(o.tekst)}
                                </option>
                              ))}
                            </select>
                          </Veld>
                        ),
                      )
                    : null}

                  {opties ? (
                    <Veld label="Vraag 7 Aard van de onderneming">
                      <select
                        value={keuzeState?.activiteit ?? 0}
                        onChange={(e) =>
                          zetKeuze({ activiteit: Number(e.target.value) })
                        }
                        disabled={downloading}
                        className={inputClass}
                      >
                        {opties.activiteiten.map((o) => (
                          <option key={o.index} value={o.index}>
                            {kort(o.tekst)}
                          </option>
                        ))}
                      </select>
                    </Veld>
                  ) : null}

                  {opties &&
                  keuzeState?.activiteit === opties.activiteiten.length - 1 ? (
                    <Veld label="Omschrijving van de activiteit">
                      <input
                        type="text"
                        value={keuzeState?.activiteitOmschrijving ?? ""}
                        onChange={(e) =>
                          setKeuzeState((prev) => ({
                            ...(prev ?? {}),
                            activiteitOmschrijving: e.target.value,
                          }))
                        }
                        onBlur={herbereken}
                        disabled={downloading}
                        className={inputClass}
                      />
                    </Veld>
                  ) : null}

                  <Veld
                    label="Vraag 8d Aantal opdrachtgevers of afnemers"
                    hint="Blijft normaal leeg"
                  >
                    <input
                      type="text"
                      inputMode="numeric"
                      value={keuzeState?.aantalAfnemers ?? ""}
                      onChange={(e) =>
                        setKeuzeState((prev) => ({
                          ...(prev ?? {}),
                          aantalAfnemers: e.target.value,
                        }))
                      }
                      onBlur={herbereken}
                      disabled={downloading}
                      className={inputClass}
                    />
                  </Veld>

                  {opties ? (
                    <fieldset>
                      <legend className="text-xs font-semibold text-ink-subtlest">
                        Vraag 10 Stukken bijvoegen
                      </legend>
                      <div className="mt-1 space-y-1">
                        {opties.stukken.map((o) => (
                          <label
                            key={o.index}
                            className="flex items-start gap-2 text-sm text-ink"
                          >
                            <input
                              type="checkbox"
                              checked={(keuzeState?.stukken ?? []).includes(
                                o.index,
                              )}
                              onChange={(e) => {
                                const huidig = keuzeState?.stukken ?? [];
                                zetKeuze({
                                  stukken: e.target.checked
                                    ? [...huidig, o.index]
                                    : huidig.filter((i) => i !== o.index),
                                });
                              }}
                              disabled={downloading}
                              className="mt-0.5 h-4 w-4 rounded border-line text-brand focus:ring-0"
                            />
                            <span>{kort(o.tekst, 120)}</span>
                          </label>
                        ))}
                      </div>
                    </fieldset>
                  ) : null}
                </div>

                <p className="mt-3 border-t border-line pt-3 text-xs text-ink-subtlest">
                  Vraag 11 wordt ondertekend door {input.adviseur.contactpersoon} (
                  {input.adviseur.naam}) met de datum van vandaag,{" "}
                  {toonDatum(input.ondertekendatum)}. Het handtekeningvak blijft
                  leeg om zelf te ondertekenen na het printen.
                </p>
              </details>
            </>
          ) : null}
        </div>

        <div className="flex items-center justify-between gap-3 px-6 pb-6 pt-2">
          <p className="text-xs text-ink-subtlest">
            {preview?.bestandsnaam
              ? `Bestandsnaam: ${preview.bestandsnaam}`
              : ""}
          </p>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onClose}
              disabled={downloading}
              className="h-8 rounded bg-[#091E420F] px-3 text-sm font-medium text-ink hover:bg-[#091E4224] disabled:cursor-not-allowed disabled:opacity-50"
            >
              Annuleer
            </button>
            {/*
              onMouseDown wordt tegengehouden zodat een klik vanuit een
              invoerveld niet verloren gaat: de blur zou anders eerst een
              herberekening starten en de knop onder de cursor vervangen.
            */}
            {geblokkeerd ? (
              <button
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => download(true)}
                disabled={downloading}
                className="h-8 rounded bg-[#C9372C] px-3 text-sm font-medium text-white transition hover:bg-[#AE2A19] disabled:cursor-not-allowed disabled:opacity-50"
                title="Genereert het formulier ondanks de gemelde problemen"
              >
                {downloading ? "Genereren…" : "Toch genereren"}
              </button>
            ) : (
              <button
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => download(false)}
                disabled={downloading || !input}
                className="h-8 rounded bg-brand px-3 text-sm font-medium text-white transition hover:bg-brand-hover disabled:cursor-not-allowed disabled:opacity-50"
              >
                {downloading ? "Genereren…" : "Genereer PDF"}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default BelastingdienstFormModal;
